import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import Toast from '../../../components/ui/Toast';
import { useToast } from '../../../hooks/useToast';

export default function BarberServices() {
  const [activeMenu, setActiveMenu] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState({ name: '', price: '', duration: '45 min' });
  const [userId, setUserId] = useState(null);
  
  const { toast, showToast, hideToast } = useToast();
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data, error } = await supabase
          .from('services_1740423033529')
          .select('*')
          .eq('barber_id', user.id)
          .order('created_at', { ascending: false });
        
        if (!error) setServices(data || []);
      }
      setLoading(false);
    };

    fetchServices();
  }, []);

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({ name: service.name, price: service.price, duration: service.duration });
    setShowModal(true);
    setActiveMenu(null);
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmModal(id);
    setActiveMenu(null);
  };

  const executeDelete = async () => {
    if (!deleteConfirmModal) return;
    const { error } = await supabase.from('services_1740423033529').delete().eq('id', deleteConfirmModal);
    if (!error) {
      setServices(services.filter(s => s.id !== deleteConfirmModal));
      showToast('Service removed successfully.', 'success');
    } else {
      showToast('Error removing service: ' + error.message, 'error');
    }
    setDeleteConfirmModal(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;

    const payload = {
      name: formData.name,
      price: Number(formData.price),
      duration: formData.duration,
      barber_id: userId
    };

    if (editingService) {
      const { error } = await supabase.from('services_1740423033529').update(payload).eq('id', editingService.id);
      if (error) showToast(error.message, 'error');
      else showToast('Service updated.', 'success');
    } else {
      const { data, error } = await supabase.from('services_1740423033529').insert([payload]).select();
      if (error) showToast(error.message, 'error');
      else if (data) {
        setServices([data[0], ...services]);
        showToast('Service added.', 'success');
      }
    }

    setShowModal(false);
    setEditingService(null);
    setFormData({ name: '', price: '', duration: '45 min' });

    // Refresh list to be sure
    const { data } = await supabase.from('services_1740423033529').select('*').eq('barber_id', userId).order('created_at', { ascending: false });
    setServices(data || []);
  };

  return (
    <div className="animate-in fade-in duration-500 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Your Portfolio</h1>
          <p className="text-gray-500 mt-1 font-medium italic">Manage the services you offer to clients.</p>
        </div>
        <Button onClick={() => {
          setEditingService(null);
          setFormData({ name: '', price: '', duration: '45 min' });
          setShowModal(true);
        }}>
          <SafeIcon name="Plus" className="mr-2" /> New Service
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400">Updating catalog...</div>
      ) : services.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-[2.5rem] border border-dashed border-gray-200">
          <SafeIcon name="Scissors" className="text-gray-300 text-3xl mx-auto mb-4" />
          <p className="text-gray-400 font-bold">No services added yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div key={service.id} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm hover:border-brand-primary transition-all group relative">
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-2xl flex items-center justify-center text-brand-primary">
                  <SafeIcon name="Scissors" />
                </div>
                <div className="relative">
                  <button onClick={() => setActiveMenu(activeMenu === service.id ? null : service.id)} className="p-2 text-gray-400 hover:text-brand-dark rounded-lg hover:bg-gray-100">
                    <SafeIcon name="MoreVertical" />
                  </button>
                  {activeMenu === service.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2">
                      <button onClick={() => handleEdit(service)} className="w-full text-left px-4 py-2.5 text-xs font-bold hover:bg-gray-50 flex items-center gap-2">
                        Modify Details
                      </button>
                      <button onClick={() => handleDeleteClick(service.id)} className="w-full text-left px-4 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 flex items-center gap-2">
                        Remove Service
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <h3 className="text-lg font-black text-brand-dark mb-1">{service.name}</h3>
              <p className="text-xs text-gray-500 mb-4 font-bold uppercase">{service.duration}</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                <span className="text-2xl font-black text-brand-dark">Rs. {Number(service.price).toLocaleString()}</span>
                <span className="text-[10px] font-black px-3 py-1 rounded-full text-green-600 bg-green-50 border border-green-100">LIVE</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Service Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-10 shadow-2xl animate-in zoom-in duration-300">
            <h2 className="text-2xl font-black mb-8 text-brand-dark tracking-tight">
              {editingService ? 'Update Service' : 'New Offering'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Service Name</label>
                <input 
                  type="text" 
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-bold"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Price (LKR)</label>
                  <input 
                    type="number" 
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Duration</label>
                  <select 
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-bold"
                  >
                    <option>15 min</option>
                    <option>30 min</option>
                    <option>45 min</option>
                    <option>60 min</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-4 mt-10">
                <Button type="button" variant="ghost" className="flex-1 font-bold text-gray-500" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 font-black shadow-xl">Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="AlertTriangle" className="text-3xl" />
            </div>
            <h3 className="text-xl font-black text-brand-dark mb-2">Remove Service?</h3>
            <p className="text-gray-500 text-sm mb-6">Are you sure you want to permanently remove this service?</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirmModal(null)}>Cancel</Button>
              <Button variant="danger" className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={executeDelete}>Remove</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}